import type { JobFileViewModel } from "@models/job";
import type { MappingResultRowTm } from "@models/mappingResult";
import { TM_VARIANT_FIELDS } from "@utils/variantLabel";

// Same columns and grouping as the TM Excel export. A row's vehicle is its
// model code and engine: the model code and engine code when printed, plus
// displacement, fuel, cylinders and engine type. Nothing else is shown —
// manufacturer, model, grade and vehicle type only repeated what the batch
// already says.
// Historical nine-part keys fill only their first nine positions; free-text
// variants stay distinct.
export const TM_BATCH_GROUP_FIELDS = [
  { label: "Model code", index: 4 },
  { label: "Engine code", index: 5 },
  { label: "Displacement (cc)", index: 6 },
  { label: "Fuel", index: 7 },
  { label: "Cylinders", index: 12 },
  { label: "Engine type", index: 14 },
] as const;

const year = (value: unknown): number | null => {
  const text = String(value ?? "").trim();
  return /^[0-9]{4}$/.test(text) ? Number(text) : null;
};

const month = (value: unknown): number | null => {
  const text = String(value ?? "").trim();
  return /^[0-9]{1,2}$/.test(text) && Number(text) >= 1 && Number(text) <= 12 ? Number(text) : null;
};

const UNDATED: TmFilePeriod = [10_000, 13, 10_000, 13];

/** [year from, month from, year to, month to]: the workbook's file sort key. */
type TmFilePeriod = [number, number, number, number];

/**
 * A row's document period, as the workbook sorts it (``batch_export.file_period``):
 * a missing month sorts first at its start and last at its end, an open end
 * after every closed one. Only the document period counts, never the vehicle's
 * own model year.
 */
const filePeriod = (variant: MappingResultRowTm["source"]["variant"]): TmFilePeriod | null => {
  if (!variant) return null;
  if (typeof variant === "string") {
    const parts = variant.split("|");
    if (parts.length !== 19) return null;
    const from = year(parts[17]);
    const to = year(parts[18]);
    return from === null ? null : [from, 0, to ?? 10_000, 13];
  }
  const from = year(variant.vehicle_year_from ?? variant.year_from);
  if (from === null) return null;
  const to = year(variant.vehicle_year_to ?? variant.year_to);
  return [
    from,
    month(variant.vehicle_month_from) ?? 0,
    to ?? 10_000,
    to === null ? 13 : (month(variant.vehicle_month_to) ?? 13),
  ];
};

const comparePeriods = (left: TmFilePeriod, right: TmFilePeriod): number =>
  left[0] - right[0] || left[1] - right[1] || left[2] - right[2] || left[3] - right[3];

/** Match the workbook's file-column ordering: earliest period first (year, then month). */
export const orderTmBatchFiles = (
  files: JobFileViewModel[],
  rows: MappingResultRowTm[],
): JobFileViewModel[] => {
  const datedFiles = new Set(files.filter((file) => file.status !== "Failed").map((file) => file.fileIndex));
  const periodByFile = new Map<number, TmFilePeriod>();
  for (const row of rows) {
    const fileId = row.fileIndex;
    if (!datedFiles.has(fileId)) continue;
    const period = filePeriod(row.source.variant);
    if (!period) continue;
    const previous = periodByFile.get(fileId);
    if (!previous || comparePeriods(period, previous) < 0) periodByFile.set(fileId, period);
  }
  return files.map((file, index) => ({ file, index })).sort((left, right) =>
    comparePeriods(periodByFile.get(left.file.fileIndex) ?? UNDATED,
      periodByFile.get(right.file.fileIndex) ?? UNDATED) || left.index - right.index,
  ).map(({ file }) => file);
};

export interface TmBatchGroup {
  key: string;
  vehicleKey: string;
  identity: string[];
  itemMpListing: string | null;
  itemMpId: number | null;
  byFile: Map<number, MappingResultRowTm[]>;
}

export const groupTmBatchRows = (
  rows: MappingResultRowTm[],
  files: JobFileViewModel[],
): { groups: TmBatchGroup[]; unattributed: MappingResultRowTm[] } => {
  const fileIds = new Set(files.map((file) => file.fileIndex));
  const grouped = new Map<string, TmBatchGroup>();
  const unattributed: MappingResultRowTm[] = [];

  for (const row of rows) {
    const fileId = row.fileIndex;
    if (!fileIds.has(fileId)) {
      unattributed.push(row);
      continue;
    }

    const variant = row.source.variant;
    const parts = typeof variant === "string" ? variant.split("|") : null;
    const parseable = variant !== null && typeof variant !== "string"
      || parts?.length === 9 || parts?.length === 17 || parts?.length === 19;
    const identity = TM_BATCH_GROUP_FIELDS.map(({ index }) =>
      !parseable ? "" : (typeof variant === "string"
        ? (parts?.[index] ?? "") : (variant?.[TM_VARIANT_FIELDS[index]] ?? "")).trim());
    const legacyVariant = !parseable && typeof variant === "string" ? variant : "";
    // A row the vehicle map placed on no engine (a grade-only scope such as
    // "SV6 Sedan") has nothing to compare by; it stays in the row review only.
    // A model code alone does not make an engine.
    const hasEngine = identity.some((field, position) =>
      TM_BATCH_GROUP_FIELDS[position].index !== 4 && field);
    if (!legacyVariant && !hasEngine) continue;
    const vehicleKey = JSON.stringify([identity, legacyVariant]);
    const itemMpListing = row.target?.itemMpListing ?? null;
    const itemMpId = row.target?.itemMpId ?? null;
    // A row without a catalogue item is still independently reviewable; do not
    // merge unrelated misses merely because both have an empty target.
    const unassigned = itemMpId == null && !itemMpListing ? row.rowIndex : null;
    const key = JSON.stringify([vehicleKey, itemMpListing, itemMpId, unassigned]);
    let group = grouped.get(key);
    if (!group) {
      group = { key, vehicleKey, identity, itemMpListing, itemMpId, byFile: new Map() };
      grouped.set(key, group);
    }
    const fileRows = group.byFile.get(fileId) ?? [];
    fileRows.push(row);
    group.byFile.set(fileId, fileRows);
  }
  for (const group of grouped.values()) {
    for (const fileRows of group.byFile.values()) fileRows.sort((a, b) => a.rowIndex - b.rowIndex);
  }

  const groups = [...grouped.values()].sort((a, b) =>
    a.identity.join("|").localeCompare(b.identity.join("|")) ||
    a.vehicleKey.localeCompare(b.vehicleKey) ||
    (a.itemMpListing ?? "").localeCompare(b.itemMpListing ?? "") ||
    (a.itemMpId ?? 0) - (b.itemMpId ?? 0),
  );
  return { groups, unattributed };
};

/** One visible value of one file: the rows showing it, and the key it is
 *  compared on (value text + unit). ``null`` for a row that is never equal to
 *  another — a failed row, or one with nothing to show. */
export interface FileValueGroup {
  key: string | null;
  rows: MappingResultRowTm[];
}

/** A compare line: what each file shows on it (absent = nothing). */
export type CompareLine = Map<number, MappingResultRowTm[]>;

/**
 * Lay one catalogue item's per-file values out as table lines.
 *
 * - A value printed by two or more files gets ONE line, so equal values always
 *   sit side by side, whatever order each PDF listed them in.
 * - A value only one file prints has no partner to line up with; those are
 *   paired across files by position, so ``8,10`` (file 1) and ``8,40`` (file 2)
 *   share a line as the difference they are.
 * - ``differentOnly`` drops every value that ALL comparable files print: what
 *   is left is exactly what differs.
 */
export const alignCompareLines = (
  byFile: Map<number, FileValueGroup[]>,
  fileOrder: number[],
  comparable: number[],
  differentOnly: boolean,
): CompareLine[] => {
  const filesOf = new Map<string, Set<number>>();
  for (const fileId of fileOrder) {
    for (const group of byFile.get(fileId) ?? []) {
      if (group.key === null) continue;
      const seen = filesOf.get(group.key) ?? new Set<number>();
      seen.add(fileId);
      filesOf.set(group.key, seen);
    }
  }
  const inEveryFile = (key: string) =>
    comparable.length > 1 && comparable.every((fileId) => filesOf.get(key)?.has(fileId));

  const lines: CompareLine[] = [];
  const lineOf = new Map<string, CompareLine>();
  const unpaired = new Map<number, MappingResultRowTm[][]>();
  for (const fileId of fileOrder) {
    for (const group of byFile.get(fileId) ?? []) {
      if (group.key !== null && (filesOf.get(group.key)?.size ?? 0) > 1) {
        if (differentOnly && inEveryFile(group.key)) continue;
        let line = lineOf.get(group.key);
        if (!line) {
          line = new Map();
          lineOf.set(group.key, line);
          lines.push(line);
        }
        line.set(fileId, group.rows);
      } else {
        const own = unpaired.get(fileId) ?? [];
        own.push(group.rows);
        unpaired.set(fileId, own);
      }
    }
  }
  const pairs = Math.max(0, ...[...unpaired.values()].map((own) => own.length));
  for (let position = 0; position < pairs; position += 1) {
    const line: CompareLine = new Map();
    for (const fileId of fileOrder) {
      const rows = unpaired.get(fileId)?.[position];
      if (rows) line.set(fileId, rows);
    }
    lines.push(line);
  }
  return lines;
};
